import type { ComponentProps, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Palette, Theme } from '@/styles/axes'
import { cx } from '../cx'
import { SectionHeader } from '../SectionHeader/SectionHeader'
import styles from './Console.module.css'

/** The first stop in the tab order; visible only while focused. */
export function SkipLink({
  href = '#main',
  children,
}: {
  href?: string
  children: ReactNode
}) {
  return (
    <a href={href} className={styles.skip}>
      {children}
    </a>
  )
}

/** The mobile bar that holds the rail's Menu button. */
export function TopBar({ children }: { children: ReactNode }) {
  return <div className={styles.topBar}>{children}</div>
}

/** The page's top band; `children` is the Breadcrumbs. */
export function Band({
  lead,
  actions,
  children,
}: {
  lead?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <div className={styles.band}>
      {lead && <span className={styles.bandLead}>{lead}</span>}
      <div className={styles.trail}>{children}</div>
      {actions && <span className={styles.bandActions}>{actions}</span>}
    </div>
  )
}

/** Console card: the side panel region (`page`, divided by a `line`) and the rule under the page header (D138). */
export function SidePanel({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <aside aria-label={label} className={cx(styles.sidePanel, className)}>
      {children}
    </aside>
  )
}

/** Console card: the side panel region (`page`, divided by a `line`) and the rule under the page header (D138). */
export function Rule({ className }: { className?: string }) {
  return (
    // biome-ignore lint/a11y/noAriaHiddenOnFocusable: an hr is not focusable; biome lists it as interactive
    <hr aria-hidden="true" className={cx(styles.rule, className)} />
  )
}

export function RailNav({
  label,
  placement,
  children,
}: {
  label: string
  placement: 'permanent' | 'drawer'
  children: ReactNode
}) {
  return (
    <nav
      aria-label={label}
      className={styles.rail}
      data-placement={placement}
      data-surface={placement === 'permanent' ? 'well' : undefined}
    >
      {children}
    </nav>
  )
}

/**
 * `trailingSpacing` is the header's own space before the first section: a
 * declared prop, as SectionHeader's (grid-and-alignment · Rhythm).
 */
export function RailHeader({
  title,
  trailingSpacing = 'compact',
  children,
}: {
  title?: string
  trailingSpacing?: 'none' | 'compact'
  children?: ReactNode
}) {
  return (
    <div className={styles.header} data-trailing={trailingSpacing}>
      {title && <p className={styles.headerTitle}>{title}</p>}
      {children}
    </div>
  )
}

/** Spreads `rest` (including `ref`) so Menu and Popover can render it as their trigger. */
export function RailButton({ children, ...rest }: ComponentProps<'button'>) {
  return (
    <button type="button" {...rest} className={styles.railButton}>
      <span className={styles.railButtonContent}>{children}</span>
    </button>
  )
}

export function RailSection({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <SectionHeader as="h2" className={styles.sectionTitle}>
          {title}
        </SectionHeader>
        {action}
      </div>
      <div className={styles.rows}>{children}</div>
    </section>
  )
}

export function RailRow({
  href,
  current,
  trailing,
  action,
  children,
  ...rest
}: Omit<
  ComponentProps<typeof Link>,
  'to' | 'className' | 'style' | 'children'
> & {
  href: string
  current?: boolean
  trailing?: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  const row = (
    <Link
      {...rest}
      to={href}
      aria-current={current ? 'page' : undefined}
      className={styles.row}
    >
      <span className={styles.rowLabel}>{children}</span>
      {trailing && <span className={styles.rowTrailing}>{trailing}</span>}
    </Link>
  )
  return action ? (
    <div className={styles.rowWithAction}>
      {row}
      {action}
    </div>
  ) : (
    row
  )
}

export function RailMore({
  onClick,
  children,
}: {
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button type="button" onClick={onClick} className={styles.more}>
      <span>{children}</span>
    </button>
  )
}

/**
 * `leadingSpacing` is the block's own space above its rule, kept when the
 * spacer collapses: a declared prop (grid-and-alignment · Rhythm).
 */
export function RailDivided({
  row,
  push,
  leadingSpacing = 'compact',
  children,
}: {
  row?: boolean
  push?: boolean
  leadingSpacing?: 'none' | 'compact'
  children: ReactNode
}) {
  const block = (
    <div
      className={styles.divided}
      data-row={row ? '' : undefined}
      data-leading={leadingSpacing}
    >
      {children}
    </div>
  )
  if (!push) return block
  return (
    <>
      <div className={styles.spacer} aria-hidden="true" />
      {block}
    </>
  )
}

export function PaletteSwatch({
  palette,
  theme,
}: {
  palette: Palette
  theme: Theme
}) {
  return (
    <span
      className={styles.swatch}
      data-palette={palette}
      data-theme={theme}
      aria-hidden="true"
    />
  )
}
